/**
 * Front-elevation layout — pure geometry, no DOM.
 *
 * Turns the plan's wall chain into a developed front elevation: a left-to-right
 * strip of panels, one per wall flagged for elevation, with door openings shown
 * between them. This is the deterministic, generated view the owner wants to
 * "lock" and later draw designs over — so it must be a pure function of the
 * room, which is exactly what this is.
 *
 * COORDINATES. Everything here is in the room's unit of measure (inches),
 * floor-up: y = 0 is the finished floor, y increases UPWARD. A wall panel runs
 * from the floor to the ceiling height; a door runs from the floor to the door
 * height; a reference line sits at its height above the floor. The renderer
 * flips y for SVG (which grows downward) and scales the whole thing to fit —
 * because width and height share these units, the result is inherently to
 * scale (ADR: elevation_scale_px_per_inch is the fixed export scale; on screen
 * we fit-to-view with equal axes).
 *
 * Openings (doors) are NOT drawn in the elevation (project owner, 2026-07-23):
 * the elevation shows wall surfaces only, so an opening contributes no panel and
 * takes no space. Wall length comes from the measured value when present, else
 * from the solved pixel geometry converted through pxPerUnit.
 */

const DEFAULT_PANEL_GAP_UNITS = 6;

function segmentLengthUnits(segment, pxPerUnit) {
  if (segment.isMeasured && segment.lengthUnits != null) return segment.lengthUnits;
  const px = Math.hypot(segment.end.x - segment.start.x, segment.end.y - segment.start.y);
  return pxPerUnit > 0 ? px / pxPerUnit : px;
}

/**
 * @param {object[]} segments the wall chain in sequence order (walls + openings)
 * @param {object} options
 * @param {number} options.pxPerUnit
 * @param {number} options.ceilingHeightUnits
 * @param {Array<{offset_from_floor_units:number, style?:string, label?:string, kind?:string}>} [options.referenceLines]
 * @param {number} [options.panelGapUnits] visual gap between panels, in units
 * @returns {{
 *   panels: Array<{kind:'wall', id:string, wallNumber:number|null, lengthUnits:number, x:number, width:number, height:number}>,
 *   referenceLines: Array<{y:number, style:string, label:string, kind:string}>,
 *   totalWidth:number, totalHeight:number, ceilingHeightUnits:number
 * }}
 */
export function layoutElevation(segments, options = {}) {
  const pxPerUnit = options.pxPerUnit ?? 3;
  const ceilingHeightUnits = options.ceilingHeightUnits ?? 118;
  // Sloped ceilings (ADR 0044): the caller supplies each wall's height profile.
  // Without one, a wall is flat at the room's ceiling height — which is every
  // room until a corner height is set, so this stays the common path.
  const profileFor = typeof options.profileFor === 'function' ? options.profileFor : null;
  const gap = options.panelGapUnits ?? DEFAULT_PANEL_GAP_UNITS;
  const referenceLines = options.referenceLines ?? [];

  const panels = [];
  let x = 0;
  for (const segment of segments) {
    const kind = segment.segmentKind ?? 'wall';

    // Only walls are elevated. Openings (doors) and excluded walls are skipped
    // entirely — they contribute no panel and take no width.
    if (kind !== 'wall') continue;
    if (segment.includeInElevation === false) continue;

    const lengthUnits = segmentLengthUnits(segment, pxPerUnit);
    if (!(lengthUnits > 0)) continue;

    const raw = profileFor ? profileFor(segment.id) : null;
    const profile = Array.isArray(raw) && raw.length >= 2
      ? raw
      : [{ atUnits: 0, heightUnits: ceilingHeightUnits }, { atUnits: lengthUnits, heightUnits: ceilingHeightUnits }];
    // `height` stays the wall's TALLEST point: everything that asks a panel how
    // tall it is (fitting the drawing, the reference-line clip) means the extent.
    const height = profile.reduce((max, pt) => (pt.heightUnits > max ? pt.heightUnits : max), 0);
    panels.push({ kind: 'wall', id: segment.id, wallNumber: segment.wallNumber ?? null, lengthUnits, x, width: lengthUnits, height, profile });
    x += lengthUnits + gap;
  }

  const totalWidth = Math.max(0, x - gap); // trim the trailing gap
  const maxReference = referenceLines.reduce((m, line) => Math.max(m, line.offset_from_floor_units || 0), 0);
  // A reference line (e.g. the 110" stacked mark) can sit ABOVE the ceiling, so
  // the drawing must be tall enough to include it — as must the TALLEST wall,
  // which on a sloped ceiling can exceed the nominal room height.
  const tallestWall = panels.reduce((m, p) => (p.height > m ? p.height : m), 0);
  const totalHeight = Math.max(ceilingHeightUnits, tallestWall, maxReference);

  const refs = referenceLines.map((line) => ({
    y: line.offset_from_floor_units,
    style: line.style ?? 'solid',
    label: line.label ?? '',
    kind: line.kind ?? '',
  }));

  return { panels, referenceLines: refs, totalWidth, totalHeight, ceilingHeightUnits };
}

/**
 * The wall height shared by every panel, or null when they differ.
 *
 * The elevation dimensions the wall height ONCE when the whole room is the same
 * height (owner, 2026-07-29) — repeating "118″" over every panel is noise on a
 * drawing whose panels are all the same height by construction today. Returning
 * null is the signal to dimension each panel separately, which is what a room
 * with differing wall heights would need.
 *
 * @param {Array<{height:number}>} panels
 * @param {number} [tol] slack for float arithmetic
 * @returns {number|null}
 */
export function uniformPanelHeight(panels, tol = 0.01) {
  const list = Array.isArray(panels) ? panels.filter((p) => p && p.height > 0) : [];
  if (!list.length) return null;
  const first = list[0].height;
  return list.every((p) => Math.abs(p.height - first) <= tol) ? first : null;
}
